import { AxiosError } from "axios";

/**
 * The five things that can go wrong with an API call, from the user's point of view. Every
 * view classifies here rather than reading status codes itself: five categories across seven
 * detail views would otherwise be thirty-five chances to answer the same question differently.
 * The frontend counterpart of the backend's `CustomerResourceAccess`.
 */
export type ApiErrorKind =
  | "unauthenticated"
  | "forbidden"
  | "notFound"
  | "validation"
  | "unavailable";

/** Why the request could not be answered at all. Same message to the user, different causes. */
export type UnavailableCause = "network" | "timeout" | "server";

export interface ApiError {
  kind: ApiErrorKind;
  /** Missing when no response arrived at all. */
  status?: number;
  /** Typed code from the backend body, e.g. COST_CENTER_FORBIDDEN. */
  code?: string;
  /** Correlation id to quote to support. Only 5xx and typed errors carry one. */
  traceId?: string;
  /** Forbidden only: a cost-centre denial rather than a role denial. */
  costCenter?: boolean;
  costCenterName?: string;
  /** Unavailable only, for telemetry — the user sees one message either way. */
  cause?: UnavailableCause;
}

export const COST_CENTER_FORBIDDEN = "COST_CENTER_FORBIDDEN";

type ErrorBody = {
  error?: string;
  message?: string;
  traceId?: string;
  costCenterName?: string;
};

const bodyOf = (error: AxiosError): ErrorBody => {
  const data = error.response?.data;
  return data && typeof data === "object" ? (data as ErrorBody) : {};
};

export const classifyApiError = (error: unknown): ApiError => {
  if (!(error instanceof AxiosError)) {
    // Not an HTTP failure at all — a bug in our own code, most likely. It still has to render
    // as something, and "try again" is the only honest thing to tell the user.
    return { kind: "unavailable", cause: "server" };
  }

  const status = error.response?.status;
  const body = bodyOf(error);
  const traceId = body.traceId;

  if (status === undefined) {
    // No response: offline, DNS, CORS, the server down, or our own timeout firing.
    const timedOut =
      error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
    return { kind: "unavailable", cause: timedOut ? "timeout" : "network" };
  }

  if (status === 401) {
    return { kind: "unauthenticated", status, traceId };
  }

  if (status === 403) {
    const costCenter = body.error === COST_CENTER_FORBIDDEN;
    return {
      kind: "forbidden",
      status,
      code: body.error,
      costCenter,
      costCenterName: costCenter ? body.costCenterName : undefined,
      traceId,
    };
  }

  if (status === 404) {
    return { kind: "notFound", status, code: body.error, traceId };
  }

  // Business rules, not broken plumbing: the caller shows these in place, never as a page.
  if (status === 400 || status === 409 || status === 422) {
    return { kind: "validation", status, code: body.error, traceId };
  }

  // 5xx, and anything else we have no specific answer for.
  return { kind: "unavailable", status, cause: "server", traceId };
};

/** True for the one kind the caller must not render — the interceptor is already redirecting. */
export const isUnauthenticated = (error: unknown): boolean =>
  classifyApiError(error).kind === "unauthenticated";
